import { whenPageActive } from '@theme/hds-page-activation';
import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, CartAddEvent } from '@theme/events';
import { getBundledCartSectionIds } from '@theme/cart-section-ids';
import { whenCartBatchIdle } from '@theme/cart-batch';

/**
 * SHO-10261: lokale formatter i.p.v. paint-calculator-core — cadeaus staan ook op
 * behang-PDP’s, die de paint-bundle niet mogen laden.
 *
 * @param {number | null | undefined} cents
 * @param {string} [moneyFormat]
 * @returns {string}
 */
function formatMoneyFromCents(cents, moneyFormat = '{{amount}}') {
  if (cents == null || !Number.isFinite(cents)) return '—';

  const value = Math.abs(cents) / 100;
  const [whole, fraction = '00'] = value.toFixed(2).split('.');
  const wholeWithDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const amountComma = `${wholeWithDots},${fraction}`;
  const amount = `${whole}.${fraction}`;

  return moneyFormat
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, wholeWithDots)
    .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${fraction}`)
    .replace(/\{\{\s*amount\s*\}\}/g, amount)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole);
}

/**
 * Gratis cadeaus op de PDP — expliciete keuze vóór toevoegen (SHO-9545).
 *
 * Zie de doc-blok in `blocks/free-gifts.liquid` voor de werking. Kort:
 * de Mixed Offer Engine levert de opt-in cadeaus voor dit product; de klant vinkt aan
 * wat hij wil; bij "In winkelwagen" liften de aangevinkte cadeaus mee in één batched
 * /cart/add; de engine herrekent daarna zelf de hoeveelheid over de hele mand.
 *
 * Het blok houdt bewust geen eigen state buiten de DOM: het vinkje ís de intentie en
 * de mand is daarna de waarheid. Niets aangevinkt = niets toegevoegd = niets weggegeven.
 *
 * Meting (zelfde patroon als SHO-9521), via `Shopify.analytics.publish`:
 * `hds:gift_exposure` (blok in beeld, 1×/pagina), `hds:gift_select` (aan/uit) en
 * `hds:gift_add` (de meelift-add). Een eigen herkomst-property op de regel kan niet:
 * die zou de seed-regel laten afwijken van de regel van de engine, waarna Shopify ze
 * niet samenvoegt. Nodig is het ook niet — deze cadeaus staan op `autoAdd: false`, dus
 * hun aanwezigheid in de mand ís de keuze van de klant.
 *
 * Cadeau op orderwaarde (VALUE_THRESHOLD, MKT-1705): de regel staat op slot tot
 * mand + deze keuze de drempel haalt. De mandstand komt uit het plan dat mixed-offer.js
 * publiceert (`mixed-offer:plan` / `window.__MIXED_OFFER_LAST_PLAN__`), de keuze uit
 * variantprijs × aantal. Zit het cadeau al in de mand, dan is er ook geen vinkje.
 * `hds:gift_unlock` meet het moment dat een regel door de keuze van de klant openspringt.
 *
 * @extends {Component}
 */

const ENDPOINT = '/apps/cross-sell/items';

const MEET_PUBLISH_RETRY_MS = 50;
const MEET_PUBLISH_DEADLINE_MS = 8000;

/** Eén miniatuur in de ingeklapte kop — zo begint de titel op elke regel (behangplan,
 *  cadeaus, toebehoren) op dezelfde x. Een stapeltje van wisselende breedte doet dat niet. */
const PREVIEW_THUMB_COUNT = 1;

/** Het aantal-veld van de PDP: eerst de buybox-rij, anders de teller van het thema zelf. */
const QUANTITY_FIELD = [
  '.bbv-qty-row [name="quantity"]',
  'quantity-selector-component [name="quantity"]:not([data-cart-line])',
  'product-form-component [name="quantity"]',
  'form[action*="/cart/add"] [name="quantity"]',
].join(', ');

/**
 * A/B-armen die het thema bepaalt (`{experimentId: arm}`); zelfde haak als mixed-offer.js.
 * Zonder armen laat de engine experiment-campagnes weg (fail closed).
 * @returns {Record<string, string> | null}
 */
function experimentArms() {
  try {
    const arms = /** @type {any} */ (window).__HDS_EXPERIMENT_ARMS__;
    if (arms && typeof arms === 'object' && Object.keys(arms).length) return arms;
  } catch {
    // Geen armen = geen experiment-cadeaus; winkelen gaat gewoon door.
  }
  return null;
}

/**
 * Vult {drempel}/{huidig}/{resterend} in een tekst uit het thema.
 * @param {string} sjabloon
 * @param {Record<string, string>} waarden
 */
function vulTekst(sjabloon, waarden) {
  return String(sjabloon || '').replace(/\{(drempel|huidig|resterend)\}/g, (_, sleutel) => waarden[sleutel] ?? '');
}

/**
 * @typedef {object} GiftApiItem
 * @property {string} crossSellItemId
 * @property {string|number} productId
 * @property {string|number} variantId
 * @property {string} title
 * @property {string} image
 * @property {number} price
 * @property {number} originalPrice
 * @property {boolean} isFree
 * @property {boolean} [freeViaCode] gratis via de code GRATISLIJM (lijmkeuze-testarm)
 * @property {number} [maxQuantity]
 * @property {string} [campaignId]
 * @property {string} [campaignTitle]
 * @property {string} [quantityRuleType]
 * @property {string} [quantityRuleConfig]
 * @property {string} [discountType]
 * @property {number} [discountValue]
 */

/**
 * Spiegelt exact de properties die de engine zelf op een cadeauregel zet
 * (`mixedOffer.server.ts`). Dat is geen luxe: Shopify voegt twee regels alleen samen
 * als variant én properties identiek zijn. Wijkt onze seed-regel af, dan blijft hij
 * náást de regel van de engine staan en ziet de klant hetzelfde cadeau dubbel.
 *
 * De onderliggende waarden komen uit dezelfde databron als de serverkant, en
 * `quantityRuleConfig` is al een string — dus geen serialisatie-verschillen.
 *
 * @param {GiftApiItem} item
 * @returns {Record<string, string>}
 */
function bouwCartProperties(item) {
  /** @type {Record<string, string>} */
  const props = {
    _cross_sell: 'true',
    _is_free: item.isFree ? 'true' : 'false',
    // Lijmkeuze-testarm: gratis via de code GRATISLIJM in plaats van € 0 (SHO-10800).
    ...(item.isFree && item.freeViaCode ? { _free_via: 'code' } : {}),
  };
  if (item.discountType) props._discount_type = String(item.discountType);
  if (typeof item.discountValue === 'number') props._discount_value = String(item.discountValue);
  if (typeof item.maxQuantity === 'number') props._max_quantity = String(item.maxQuantity);
  if (item.quantityRuleType) props._quantity_rule_type = String(item.quantityRuleType);
  if (item.quantityRuleConfig) props._quantity_rule_config = String(item.quantityRuleConfig);
  return props;
}

/**
 * Bewaar de trigger-context van dit product waar `mixed-offer.js` hem zoekt.
 *
 * Bij elke `cart:update` rekent mixed-offer.js de mand door via `/apps/cross-sell/compute`
 * en stuurt daarbij de context van de regels mee. Ontbreekt die, dan moet de app zelf
 * opzoeken in welke collecties het product zit — en dáár zit het gat: de merkcollecties
 * van Arte en Hookedonwalls komen niet terug in de Admin-API, dus de campagne matcht niet.
 * Resultaat: `TRIGGER_MISSING`, de hoeveelheid lijm schaalt niet mee met het aantal rollen
 * en de cadeaus kunnen zelfs uit de mand vallen.
 *
 * Het app-block van de engine (`cross-sell-widget.js`) vult deze store zelf; sinds wij dat
 * blok op de PDP vervangen hebben door dit blok, moeten wij hem vullen. Zelfde vorm, zelfde
 * sleutels: `<productId>:<variantId>` én `<productId>:` als variant-loze fallback.
 *
 * @param {{productId?: string|number, variantId?: string|number|null}|null|undefined} context
 * @param {string} variantId  De variant waarvoor we ophaalden; de API laat `variantId` leeg.
 */
function bewaarTriggerContext(context, variantId) {
  if (!context || !context.productId) return;
  try {
    const store = (window.__MIXED_OFFER_LINE_CONTEXTS__ = window.__MIXED_OFFER_LINE_CONTEXTS__ || {});
    const productId = String(context.productId);
    const vid = String(variantId || context.variantId || '');
    const entry = { ...context, variantId: vid || null };
    if (vid) store[`${productId}:${vid}`] = entry;
    store[`${productId}:`] = entry;
  } catch {
    // Context bewaren mag winkelen nooit breken.
  }
}

/**
 * @typedef {object} GiftRow
 * @property {string} key
 * @property {string} variantId  Seed-variant die we in de mand leggen.
 * @property {string} name
 * @property {string} note
 * @property {string} image
 * @property {number} now        Actuele prijs in euro's; 0 als gratis of onbekend.
 * @property {number} was        Doorgestreepte prijs in euro's; 0 = niet tonen.
 * @property {boolean} isFree    Uit de engine; alleen dan tonen we het gratis-label.
 * @property {boolean} computed  Meerdere formaten van één actie op één regel.
 * @property {boolean} scales    De engine bepaalt het aantal; niet één per bestelling.
 * @property {Record<string, string>} properties  Cartproperties van de seed-regel.
 * @property {string} crossSellItemId  Sleutel waarop het plan van de engine de drempel meldt.
 * @property {string} campaignTitle
 * @property {number} threshold  Drempel in centen (VALUE_THRESHOLD); 0 = geen drempel.
 * @property {boolean} locked    Drempel nog niet gehaald met mand + deze keuze.
 * @property {boolean} inCart    Cadeau ligt al in de mand (uit het plan, of op zijn maximum).
 * @property {number} maxQuantity  Vast maximum per bestelling; 0 = geen of meeschalend.
 * @property {number} projectedCents  Mand + deze keuze, in centen.
 */

/**
 * Meet-event naar de Shopify Web Pixels. `Shopify.analytics.publish` staat vaak pas ná
 * de theme-modules klaar, dus wachten we er kort op. Meten mag winkelen nooit breken.
 * @param {string} naam
 * @param {Record<string, unknown>} payload
 */
function publiceerMeetEvent(naam, payload) {
  if (document.prerendering) {
    whenPageActive(() => publiceerMeetEvent(naam, payload));
    return;
  }
  const deadline = Date.now() + MEET_PUBLISH_DEADLINE_MS;

  const tick = () => {
    try {
      const publish = window.Shopify?.analytics?.publish;
      if (typeof publish === 'function') {
        publish(naam, payload);
        return;
      }
    } catch {
      return;
    }
    if (Date.now() > deadline) return;
    setTimeout(tick, MEET_PUBLISH_RETRY_MS);
  };

  tick();
}

/** Woorden die aan het eind van een afgeleide groepsnaam blijven hangen. */
const VERBINDINGSWOORDEN = new Set([
  'voor', 'van', 'per', 'met', 'tot', 'in', 'op',
  // Ook de bij-de-hand-woorden vóór het getal, anders blijft er "…voor ca." staan.
  'ca.', 'ca', 'ong.', 'ongeveer', '±',
]);

/**
 * Noemer voor een groep formaten waarvan de campagne zelf geen titel heeft:
 * het stuk dat alle titels delen. "Professionele behanglijm voor 2 rollen (12 m²)"
 * en zijn broertjes worden zo samen "Professionele behanglijm".
 *
 * Alleen een vangnet — een campagnetitel in de engine gaat altijd voor.
 *
 * @param {string[]} titels
 * @returns {string}
 */
function gedeeldeNaam(titels) {
  if (titels.length < 2) return '';

  let prefix = titels[0] ?? '';
  for (const titel of titels.slice(1)) {
    let i = 0;
    while (i < prefix.length && i < titel.length && prefix[i] === titel[i]) i++;
    prefix = prefix.slice(0, i);
  }

  // Afkappen op een woordgrens, zodat er geen half woord overblijft.
  const grens = prefix.lastIndexOf(' ');
  if (grens < 0) return '';

  const woorden = prefix
    .slice(0, grens)
    .replace(/[\s\-–—,:(]+$/, '')
    .split(' ');
  while (woorden.length > 1 && VERBINDINGSWOORDEN.has(woorden[woorden.length - 1].toLowerCase())) {
    woorden.pop();
  }

  const naam = woorden.join(' ').trim();
  return naam.length >= 4 ? naam : '';
}

/**
 * Korte noemer voor de ingeklapte kop — dezelfde afkapping als het toebehoren-blok
 * (alles vóór ' - '), zodat de twee koppen onder elkaar hetzelfde lezen.
 * @param {string} naam
 * @returns {string}
 */
function kortNaam(naam) {
  return naam.split(' - ')[0].trim();
}

/**
 * `quantityRuleConfig` komt als JSON-string uit de engine.
 * @param {string | undefined} raw
 * @returns {Record<string, any> | null}
 */
function parseRuleConfig(raw) {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** @param {unknown} error */
function foutTekst(error) {
  return error instanceof Error ? error.message : String(error);
}

export class FreeGifts extends Component {
  /** @type {GiftRow[]} */
  #rows = [];

  /** @type {IntersectionObserver | undefined} */
  #exposureObserver;

  #exposureGemeld = false;

  /** @type {AbortController | undefined} */
  #ophalen;

  /** @type {string} */
  #geladenVoorVariant = '';

  /** @type {(() => void) | undefined} */
  #teardownVariantInput;

  /**
   * Laatst gepubliceerde drempelstand uit de engine, per crossSellItemId.
   * @type {Map<string, {currentCents: number, inCart: boolean, thresholdCents: number}>}
   */
  #drempelstand = new Map();

  /** Regels die al eens open zijn gemeten (`hds:gift_unlock` is 1× per regel per pagina). */
  #ontgrendeldGemeld = new Set();

  /** @type {Promise<boolean> | null} Lopende meelift-add; één per hoofd-add. */
  #batchBezig = null;

  /**
   * Aantal cadeauregels per variant in de mand (alleen `_cross_sell`-regels).
   * @type {Map<string, number>}
   */
  #mandCadeaus = new Map();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #mandTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
    document.addEventListener('addToCart:success', this.#onConfiguratorCartSuccess);
    document.addEventListener('mixed-offer:plan', this.#onPlan);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onAantalWijziging);
    document.addEventListener('change', this.#onAantalWijziging);
    document.addEventListener('input', this.#onAantalWijziging);
    this.#bindVariantInputFallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onMandWijziging);
    this.#leesPlan(/** @type {any} */ (window).__MIXED_OFFER_LAST_PLAN__);
    void this.#leesMand();

    void this.#laad(this.#resolveMainVariantId() ?? this.dataset.initialVariantId ?? '');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    document.removeEventListener('addToCart:success', this.#onConfiguratorCartSuccess);
    document.removeEventListener('mixed-offer:plan', this.#onPlan);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onAantalWijziging);
    document.removeEventListener('change', this.#onAantalWijziging);
    document.removeEventListener('input', this.#onAantalWijziging);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onMandWijziging);
    clearTimeout(this.#mandTimer);
    this.#ophalen?.abort();
    this.#teardownVariantInput?.();
    this.#teardownVariantInput = undefined;
    this.#exposureObserver?.disconnect();
    this.#exposureObserver = undefined;
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    void this.#laad(this.#resolveMainVariantId() ?? this.dataset.initialVariantId ?? '');
  }

  // ---------------------------------------------------------------- ophalen

  /**
   * Haal de opt-in cadeaus voor deze variant op bij de Mixed Offer Engine.
   * @param {string} variantId
   */
  async #laad(variantId) {
    const productId = this.dataset.productId;
    if (!productId) return;
    if (variantId && variantId === this.#geladenVoorVariant) return;

    this.#ophalen?.abort();
    const controller = new AbortController();
    this.#ophalen = controller;

    const collectionIds = (this.dataset.collectionIds || '').split(',').filter(Boolean);
    const productTags = (this.dataset.productTags || '').split(',').filter(Boolean);

    /** @type {Record<string, unknown>} */
    const body = {
      productId,
      variantId: variantId || null,
      collectionIds,
      productTags,
      maxItems: Number(this.dataset.maxItems) || 6,
    };
    const arms = experimentArms();
    if (arms) body.experiments = arms;

    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Cadeaus ophalen mislukt (${response.status})`);

      const data = await response.json();
      if (controller.signal.aborted) return;

      bewaarTriggerContext(data.triggerContext, variantId);

      this.#geladenVoorVariant = variantId;
      this.#rows = this.#bouwRijen(Array.isArray(data.items) ? data.items : []);
      this.#render();
    } catch (error) {
      if (controller.signal.aborted) return;
      // Geen cadeaus tonen is beter dan een halve belofte tonen.
      this.#rows = [];
      this.#render();
      console.warn('Gratis cadeaus — ophalen mislukt:', error);
    }
  }

  /**
   * Zet de losse engine-items om in getoonde regels.
   *
   * Formaten van dezelfde AREA_BUCKETS-campagne (LIJM2/5/15) zijn samen één cadeau: de
   * klant kiest "lijm", de engine kiest de emmers. Zonder `campaignId`/`quantityRuleType`
   * in de payload (oudere app-versie) valt elk item terug op een eigen regel.
   *
   * @param {GiftApiItem[]} items
   * @returns {GiftRow[]}
   */
  #bouwRijen(items) {
    const groupNote = this.dataset.groupNote || '';
    /** @type {Map<string, GiftApiItem[]>} */
    const groepen = new Map();

    for (const item of items) {
      if (!item || !item.variantId) continue;
      const isArea = String(item.quantityRuleType || '') === 'AREA_BUCKETS';
      const key = isArea && item.campaignId ? `campaign:${item.campaignId}` : `item:${item.crossSellItemId}`;
      const bestaand = groepen.get(key);
      if (bestaand) bestaand.push(item);
      else groepen.set(key, [item]);
    }

    /** @type {GiftRow[]} */
    const rows = [];

    for (const [key, groep] of groepen) {
      const computed = key.startsWith('campaign:') && groep.length > 1;
      const seed = computed ? this.#kiesSeed(groep) : groep[0];
      if (!seed) continue;

      // Ook één los product kan meeschalen: bij STEPWISE geeft de engine een pakje per
      // zoveel rollen en telt `maxQuantity` niet mee. "Eén per bestelling" eronder zetten
      // zou dan een belofte breken die de mand wél nakomt.
      const rekenregel = String(seed.quantityRuleType || '');
      const scales =
        computed || rekenregel === 'STEPWISE_PER_TRIGGER_ITEMS' || rekenregel === 'AREA_BUCKETS';

      // Cadeau op orderwaarde: de drempel staat in de rekenregel van het item zelf.
      const threshold =
        rekenregel === 'VALUE_THRESHOLD'
          ? Math.max(0, Math.round(Number(parseRuleConfig(seed.quantityRuleConfig)?.thresholdCents) || 0))
          : 0;

      const maxQuantity = Number(seed.maxQuantity) || 0;
      // De regel noemt het product, niet de actie: een campagnetitel als "Arte &
      // Hookedonwalls actie" hoort niet als naam boven de lijmregel te staan.
      const groepsnaam = gedeeldeNaam(groep.map((item) => item.title)) || seed.title;

      const now = computed ? 0 : Number(seed.price) || 0;
      const isFree = Boolean(seed.isFree);

      rows.push({
        key,
        variantId: String(seed.variantId),
        name: computed ? groepsnaam : seed.title,
        note: scales ? groupNote : maxQuantity === 1 ? 'Eén per bestelling' : '',
        image: seed.image || '',
        // Bij een berekend cadeau zegt de prijs van één emmer niets over wat de klant krijgt.
        now,
        was: computed ? 0 : Number(seed.originalPrice) || 0,
        isFree,
        computed,
        scales,
        properties: bouwCartProperties(seed),
        crossSellItemId: String(seed.crossSellItemId || ''),
        campaignTitle: String(seed.campaignTitle || ''),
        threshold,
        locked: threshold > 0,
        inCart: false,
        maxQuantity: scales ? 0 : maxQuantity,
        projectedCents: 0,
      });
    }

    // De API levert de campagnes niet in een vaste volgorde, dus zonder sortering
    // wisselen de cadeaus per pageload van plek.
    //
    // Meeschalende cadeaus staan bovenaan: hun hoeveelheid groeit met de bestelling
    // (lijm voor zóveel rollen), dus daar hangt de meeste waarde aan. Een cadeau van
    // één per bestelling is het extraatje ernaast. Binnen een groep beslist de naam,
    // die is stabiel én voorspelbaar.
    return rows.sort((a, b) => {
      if (a.scales !== b.scales) return a.scales ? -1 : 1;
      return a.name.localeCompare(b.name, 'nl');
    });
  }

  /**
   * Kleinste formaat als seed: de engine schaalt vanaf daar op naar de optimale combinatie.
   * @param {GiftApiItem[]} groep
   * @returns {GiftApiItem | undefined}
   */
  #kiesSeed(groep) {
    let seed;
    let laagste = Number.POSITIVE_INFINITY;

    for (const item of groep) {
      const config = parseRuleConfig(item.quantityRuleConfig);
      const gewicht = Number(config?.units) || Number(item.originalPrice) || 0;
      if (gewicht > 0 && gewicht < laagste) {
        laagste = gewicht;
        seed = item;
      }
    }

    return seed ?? groep[0];
  }

  // ---------------------------------------------------------------- renderen

  #render() {
    const list = this.querySelector('[data-fg-list]');
    const template = this.querySelector('template[data-fg-template]');
    if (!(list instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    list.replaceChildren();
    delete this.dataset.reserved;
    delete this.dataset.cached;

    if (!this.#rows.length) {
      this.hidden = true;
      return;
    }

    const preselect = this.dataset.preselect === 'true';

    for (const row of this.#rows) {
      const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const item = fragment.querySelector('[data-fg-item]');
      if (!(item instanceof HTMLElement)) continue;

      item.dataset.variantId = row.variantId;
      item.dataset.giftKey = row.key;
      item.dataset.giftName = row.name;
      item.dataset.isFree = row.isFree ? 'true' : 'false';
      item.dataset.price = String(Math.round((row.isFree ? 0 : row.now) * 100));
      if (row.threshold > 0) item.dataset.threshold = String(row.threshold);

      const check = item.querySelector('[data-fg-check]');
      if (check instanceof HTMLInputElement) {
        check.checked = preselect;
        check.setAttribute(
          'aria-label',
          row.isFree ? `Gratis meesturen: ${row.name}` : `Meesturen: ${row.name}`
        );
      }

      // Liquid zette het cadeau-teken al in de tegel. Alleen de regel van de lopende
      // actie houdt dat teken: daar staat geen enkel product vast — de engine kiest de
      // emmers pas als de mand rond is, dus één packshot zou liegen. Elke andere regel
      // krijgt zijn eigen foto, gratis of niet.
      const thumb = item.querySelector('[data-fg-thumb]');
      if (thumb instanceof HTMLElement) {
        if (row.isFree && row.computed) {
          thumb.classList.add('fg-item__thumb--actie');
        } else if (row.image) {
          const img = document.createElement('img');
          img.src = row.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 44;
          img.height = 44;
          thumb.replaceChildren(img);
        } else {
          thumb.replaceChildren();
        }
      }

      const name = item.querySelector('[data-fg-name]');
      if (name instanceof HTMLElement) name.textContent = row.name;

      const note = item.querySelector('[data-fg-note]');
      if (note instanceof HTMLElement) {
        note.textContent = row.note;
        note.hidden = !row.note;
      }

      const was = item.querySelector('[data-fg-was]');
      const nowEl = item.querySelector('[data-fg-now]');
      const freeEl = item.querySelector('[data-fg-free]');
      const showWas = row.was > 0 && (row.isFree || row.was > row.now);

      if (was instanceof HTMLElement) {
        if (showWas) {
          was.textContent = this.#formatMoney(Math.round(row.was * 100));
          was.hidden = false;
        } else {
          was.hidden = true;
        }
      }

      if (freeEl instanceof HTMLElement) {
        freeEl.textContent = this.dataset.freeLabel || 'Gratis';
        freeEl.hidden = !row.isFree;
      }

      if (nowEl instanceof HTMLElement) {
        if (!row.isFree && row.now > 0) {
          nowEl.textContent = this.#formatMoney(Math.round(row.now * 100));
          nowEl.hidden = false;
        } else {
          nowEl.hidden = true;
        }
      }

      item.classList.toggle('fg-item--selected', preselect);
      list.append(item);
    }

    this.hidden = false;
    this.#evalueerDrempels();
    this.#evalueerMand();
    this.#renderSamenvatting();
    this.#updateTeller();
    this.#observeerExposure();
  }

  // ---------------------------------------------------------------- drempel

  /** @param {Event} event */
  #onPlan = (event) => {
    this.#leesPlan(/** @type {CustomEvent} */ (event).detail);
    this.#herwaardeer();
  };

  /**
   * Alleen wijzigingen van het aantal-veld van de PDP tellen; `change`/`input` op het
   * document vangen ook typen op, de +/− knoppen melden zich via quantity-selector:update.
   * @param {Event} event
   */
  #onAantalWijziging = (event) => {
    if (event.type !== ThemeEvents.quantitySelectorUpdate) {
      const veld = event.target;
      if (!(veld instanceof Element) || !veld.matches(QUANTITY_FIELD)) return;
    }
    this.#herwaardeer();
  };

  /** @param {any} plan */
  #leesPlan(plan) {
    this.#drempelstand = new Map();
    const thresholds = Array.isArray(plan?.thresholds) ? plan.thresholds : [];
    for (const t of thresholds) {
      if (!t || !t.crossSellItemId) continue;
      this.#drempelstand.set(String(t.crossSellItemId), {
        currentCents: Math.max(0, Number(t.currentCents) || 0),
        inCart: Boolean(t.inCart),
        thresholdCents: Number(t.thresholdCents) || 0,
      });
    }
  }

  #herwaardeer() {
    if (!this.#rows.some((row) => row.threshold > 0)) return;
    this.#evalueerDrempels();
    this.#renderSamenvatting();
    this.#updateTeller();
  }

  /** Prijs in centen van de variant die nu in het formulier staat; 0 als onbekend. */
  #huidigeVariantPrijsCents() {
    const variantId = this.#resolveMainVariantId() ?? this.#geladenVoorVariant ?? this.dataset.initialVariantId ?? '';
    if (!variantId) return 0;
    const script = this.querySelector('script[data-fg-variant-prices]');
    if (!(script instanceof HTMLScriptElement)) return 0;
    try {
      const prijzen = JSON.parse(script.textContent || '{}');
      return Math.max(0, Math.round(Number(prijzen?.[String(variantId)]) || 0));
    } catch {
      return 0;
    }
  }

  #huidigAantal() {
    const scope = this.closest('.product-details') ?? document;
    const veld = scope.querySelector(QUANTITY_FIELD);
    const aantal = veld instanceof HTMLInputElement || veld instanceof HTMLSelectElement ? parseInt(veld.value, 10) : 1;
    return aantal > 0 ? aantal : 1;
  }

  /**
   * Zet per drempelregel de stand: op slot, open, of al in de mand — en schrijft dat in de rij.
   * Mand (uit het plan) + deze keuze (variantprijs × aantal) tegen de drempel.
   */
  #evalueerDrempels() {
    const drempelrijen = this.#rows.filter((row) => row.threshold > 0);
    if (!drempelrijen.length) return;

    const keuzeCents = this.#huidigeVariantPrijsCents() * this.#huidigAantal();

    for (const row of drempelrijen) {
      const stand = this.#drempelstand.get(row.crossSellItemId);
      const mandCents = stand?.currentCents ?? 0;
      const inCart = stand?.inCart ?? false;
      const wasLocked = row.locked;

      row.inCart = inCart;
      row.projectedCents = mandCents + keuzeCents;
      row.locked = !inCart && row.projectedCents < row.threshold;

      const item = this.querySelector(`[data-fg-item][data-gift-key="${CSS.escape(row.key)}"]`);
      if (item instanceof HTMLElement) this.#schrijfDrempelRij(item, row);

      if (wasLocked && !row.locked && !inCart && !this.#ontgrendeldGemeld.has(row.key)) {
        this.#ontgrendeldGemeld.add(row.key);
        publiceerMeetEvent('hds:gift_unlock', {
          cadeau: row.name,
          drempel_cents: row.threshold,
          mand_cents: mandCents,
          keuze_cents: keuzeCents,
          hoofdproduct_id: this.dataset.productId ?? '',
        });
      }
    }

    this.#toonUitleg();
  }

  /** Zonder vinkjes slaat "Laat de vinkjes uit" nergens op — intro én voettekst. */
  #toonUitleg() {
    const geenVinkjes = this.#rows.every((row) => row.locked || row.inCart);
    for (const el of this.querySelectorAll('[data-fg-intro], [data-fg-footer]')) {
      if (el instanceof HTMLElement) el.hidden = geenVinkjes || !el.textContent?.trim();
    }
  }

  // ---------------------------------------------------------------- mand

  /** Elke mandwijziging (ook onze eigen add en die van mixed-offer.js) → stand opnieuw lezen. */
  #onMandWijziging = () => {
    clearTimeout(this.#mandTimer);
    this.#mandTimer = setTimeout(() => void this.#leesMand(), 300);
  };

  async #leesMand() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      /** @type {Map<string, number>} */
      const aantallen = new Map();
      for (const line of Array.isArray(cart?.items) ? cart.items : []) {
        if (line?.properties?._cross_sell !== 'true') continue;
        const id = String(line.variant_id);
        aantallen.set(id, (aantallen.get(id) ?? 0) + (Number(line.quantity) || 0));
      }
      this.#mandCadeaus = aantallen;
      this.#evalueerMand();
    } catch {
      // Zonder mandstand blijft elk vinkje open; de los-toevoegen-terugval vangt het op.
    }
  }

  /**
   * Een cadeau van vast maximum (de behangset: één per bestelling) dat al op dat maximum
   * in de mand ligt, kan niet nog eens mee. Bleef het vinkje open, dan voegde Shopify de
   * seed samen met de bestaande regel, weigerde de checkout-regel die tweede ("slechts 1
   * artikel") en viel de rest van de batch — de lijm — stil mee weg (SHO-11755).
   */
  #evalueerMand() {
    let gewijzigd = false;
    for (const row of this.#rows) {
      if (row.threshold > 0 || !(row.maxQuantity > 0)) continue;
      const inCart = (this.#mandCadeaus.get(row.variantId) ?? 0) >= row.maxQuantity;
      if (inCart === row.inCart) continue;
      row.inCart = inCart;
      gewijzigd = true;

      const item = this.querySelector(`[data-fg-item][data-gift-key="${CSS.escape(row.key)}"]`);
      if (!(item instanceof HTMLElement)) continue;
      item.classList.toggle('fg-item--in-cart', inCart);
      item.classList.remove('fg-item--selected');

      const check = item.querySelector('[data-fg-check]');
      if (check instanceof HTMLInputElement) {
        check.checked = inCart;
        check.disabled = inCart;
      }

      const note = item.querySelector('[data-fg-note]');
      if (note instanceof HTMLElement) {
        const tekst = inCart ? 'Zit al in je winkelwagen' : row.note;
        note.textContent = tekst;
        note.hidden = !tekst;
      }
    }
    if (!gewijzigd) return;
    this.#toonUitleg();
    this.#renderSamenvatting();
    this.#updateTeller();
  }

  /**
   * @param {HTMLElement} item
   * @param {GiftRow} row
   */
  #schrijfDrempelRij(item, row) {
    const check = item.querySelector('[data-fg-check]');
    const note = item.querySelector('[data-fg-note]');
    const bedragen = {
      drempel: this.#formatMoney(row.threshold),
      huidig: this.#formatMoney(row.projectedCents),
      resterend: this.#formatMoney(Math.max(0, row.threshold - row.projectedCents)),
    };

    item.classList.toggle('fg-item--locked', row.locked);
    item.classList.toggle('fg-item--in-cart', row.inCart);
    item.dataset.locked = row.locked ? 'true' : 'false';

    if (check instanceof HTMLInputElement) {
      if (row.locked) {
        check.checked = false;
        check.disabled = true;
        item.classList.remove('fg-item--selected');
      } else if (row.inCart) {
        check.checked = true;
        check.disabled = true;
        item.classList.remove('fg-item--selected');
      } else {
        check.disabled = false;
        item.classList.toggle('fg-item--selected', check.checked);
      }
    }

    if (note instanceof HTMLElement) {
      let tekst = '';
      if (row.inCart) tekst = 'Zit al in je winkelwagen';
      else if (row.locked) tekst = vulTekst(this.dataset.thresholdLockedNote || '', bedragen);
      else tekst = vulTekst(this.dataset.thresholdReachedNote || '', bedragen) || row.note;
      note.textContent = tekst;
      note.hidden = !tekst;
    }
  }

  /**
   * Vult de ingeklapte kop: miniaturen, badge met aantal en de cadeaunamen. Vervangt
   * meteen de laadbalkjes die Liquid neerzette om de ruimte te reserveren — beide zijn
   * even hoog, dus dit verschuift niets.
   */
  #renderSamenvatting() {
    const thumbs = this.querySelector('[data-fg-thumbs]');
    if (thumbs instanceof HTMLElement) {
      const icoonTemplate = this.querySelector('template[data-fg-gift-icon]');
      const beelden = this.#rows
        .filter((row) => (row.isFree && row.computed) || row.image)
        .slice(0, PREVIEW_THUMB_COUNT)
        .map((row) => {
          // Zelfde afweging als in de rij: alleen de actieregel toont het teken.
          if (row.isFree && row.computed && icoonTemplate instanceof HTMLTemplateElement) {
            const tegel = document.createElement('span');
            tegel.className = 'fg__thumb-actie';
            tegel.append(icoonTemplate.content.cloneNode(true));
            return tegel;
          }
          const img = document.createElement('img');
          img.src = row.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 34;
          img.height = 34;
          return img;
        });
      thumbs.replaceChildren(...beelden);
    }

    // Alles op slot: de kop kondigt de actie aan in plaats van een keuze te beloven.
    const alleOpSlot = this.#rows.length > 0 && this.#rows.every((row) => row.locked);
    const slotRij = alleOpSlot ? this.#rows[0] : null;
    const slotBedragen = slotRij
      ? {
          drempel: this.#formatMoney(slotRij.threshold),
          huidig: this.#formatMoney(slotRij.projectedCents),
          resterend: this.#formatMoney(Math.max(0, slotRij.threshold - slotRij.projectedCents)),
        }
      : null;

    const heading = this.querySelector('[data-fg-heading]');
    if (heading instanceof HTMLElement) {
      const allFree = this.#rows.length > 0 && this.#rows.every((row) => row.isFree);
      const paidHeading = this.dataset.heading || '';
      const freeHeading = this.dataset.headingWhenFree || '';
      if (slotRij && slotBedragen) {
        heading.textContent =
          vulTekst(this.dataset.thresholdHeading || '', slotBedragen) || slotRij.campaignTitle || paidHeading;
      } else {
        heading.textContent = allFree ? freeHeading || paidHeading : paidHeading;
      }
    }

    const badge = this.querySelector('[data-fg-badge]');
    if (badge instanceof HTMLElement) {
      const label = this.dataset.badgeLabel || '';
      // Alleen wat de klant nú kan aanvinken telt; een cadeau op slot is nog geen cadeau.
      const freeCount = this.#rows.filter((row) => row.isFree && !row.locked).length;
      badge.textContent = label && freeCount > 0 ? `${freeCount} ${label}` : '';
      badge.hidden = !label || freeCount === 0;
    }

    const namen = this.querySelector('[data-fg-names]');
    if (namen instanceof HTMLElement) {
      const delen = this.#rows.map((row) => kortNaam(row.name));
      if (slotBedragen) {
        const kort = vulTekst(this.dataset.thresholdLockedShort || '', slotBedragen);
        if (kort) delen.push(kort);
      }
      namen.textContent = delen.join(' · ');
    }
  }

  #updateTeller() {
    const teller = this.querySelector('[data-fg-count]');
    if (!(teller instanceof HTMLElement)) return;

    const aantal = this.#geselecteerdeItems().length;
    teller.textContent = aantal === 1 ? '1 gekozen' : `${aantal} gekozen`;
    teller.hidden = aantal === 0;
  }

  // ---------------------------------------------------------------- interactie

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // Het label schakelt de checkbox zelf al; anders zou de kaartklik 'm meteen terugzetten.
    if (target.closest('.fg-item__check')) return;

    const item = target.closest('[data-fg-item]');
    if (!(item instanceof HTMLElement)) return;

    const check = item.querySelector('[data-fg-check]');
    if (!(check instanceof HTMLInputElement) || check.disabled) return;

    check.checked = !check.checked;
    this.#verwerkSelectie(item, check.checked);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-fg-check]')) return;

    const item = target.closest('[data-fg-item]');
    if (!(item instanceof HTMLElement)) return;

    this.#verwerkSelectie(item, target.checked);
  };

  /**
   * @param {HTMLElement} item
   * @param {boolean} gekozen
   */
  #verwerkSelectie(item, gekozen) {
    item.classList.toggle('fg-item--selected', gekozen);
    this.#updateTeller();

    publiceerMeetEvent('hds:gift_select', {
      gekozen,
      cadeau: item.dataset.giftName ?? '',
      variant_id: item.dataset.variantId ?? '',
      hoofdproduct_id: this.dataset.productId ?? '',
    });
  }

  /** @returns {HTMLElement[]} */
  #geselecteerdeItems() {
    return [...this.querySelectorAll('[data-fg-item]')].filter((item) => {
      if (!(item instanceof HTMLElement)) return false;
      const check = item.querySelector('[data-fg-check]');
      // Een uitgeschakeld vinkje (op slot, of al in de mand) is geen keuze.
      return check instanceof HTMLInputElement && check.checked && !check.disabled;
    });
  }

  // ---------------------------------------------------------------- variant

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;
    if (event.target instanceof Element && event.target.closest('#quick-add-dialog, .quick-add-modal')) return;

    const variantId = event.detail.resource?.id;
    if (variantId == null || variantId === '') return;
    void this.#laad(String(variantId));
  };

  #bindVariantInputFallback() {
    const scope = this.#productFormScope();
    const input = scope?.querySelector('[ref="variantId"]') ?? scope?.querySelector('input[name="id"]');
    if (!(input instanceof HTMLInputElement)) return;

    const onChange = () => {
      if (input.value) void this.#laad(input.value);
    };
    input.addEventListener('change', onChange);

    const observer = new MutationObserver(onChange);
    observer.observe(input, { attributes: true, attributeFilter: ['value'] });

    this.#teardownVariantInput = () => {
      input.removeEventListener('change', onChange);
      observer.disconnect();
    };
  }

  #productFormScope() {
    return (
      this.closest('product-form-component') ??
      this.closest('.product-information')?.querySelector('product-form-component') ??
      this.closest('.product-details')?.querySelector('product-form-component')
    );
  }

  #resolveMainVariantId() {
    const scope = this.#productFormScope();
    if (!(scope instanceof Element)) return null;
    const form = scope.querySelector('form[action*="/cart/add"]');
    if (!(form instanceof HTMLFormElement)) return null;
    return this.#resolveMainVariantIdFromForm(form);
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string | null}
   */
  #resolveMainVariantIdFromForm(form) {
    const input = form.querySelector('[ref="variantId"]') ?? form.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement && input.value) return input.value;
    return null;
  }

  // ---------------------------------------------------------------- meeliften

  /**
   * De hoofd-add is gelukt → aangevinkte cadeaus als seed meesturen.
   * @param {Event} event
   */
  #onCartAdd = async (event) => {
    if (this.hidden) return;
    if (!(event instanceof CartAddEvent)) return;

    const data = event.detail?.data ?? {};
    if (data.didError) return;
    if (data.source !== 'product-form-component') return;
    if (event.target instanceof Element && event.target.closest('.quick-add-modal, #quick-add-dialog')) return;

    const addedVariantId = String(event.detail.sourceId ?? data.variantId ?? '');
    const mainVariantId = this.#resolveMainVariantId();
    if (!addedVariantId || !mainVariantId || addedVariantId !== String(mainVariantId)) return;

    await this.#batchCadeausToCart();
  };

  /**
   * Configurator-PDP: die omzeilt het theme-form en vuurt een eigen event.
   * @param {Event} event
   */
  #onConfiguratorCartSuccess = async (event) => {
    if (this.hidden) return;

    const form = /** @type {any} */ (event).detail?.form;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.closest('.quick-add-modal, #quick-add-dialog')) return;

    const addedVariantId = this.#resolveMainVariantIdFromForm(form);
    const mainVariantId = this.#resolveMainVariantId();
    if (!addedVariantId || !mainVariantId || addedVariantId !== String(mainVariantId)) return;

    await this.#batchCadeausToCart();
  };

  /**
   * Eén seed-regel per gekozen cadeau. De hoeveelheid is bewust altijd 1: mixed-offer.js
   * laat de engine direct hierna de mand doorrekenen en herschrijft de regel naar de
   * juiste hoeveelheid over álle triggerproducten in de mand.
   * @returns {Promise<boolean>}
   */
  async #batchCadeausToCart() {
    // Het thema-formulier vuurt bij één add zowel `addToCart:success` als CartAddEvent;
    // beide komen hier. Zonder deze poort ging de seed twee keer de deur uit en weigerde
    // de Cart API de tweede ("slechts 1 artikel") — een fout die de klant niet begrijpt.
    if (this.#batchBezig) return this.#batchBezig;
    this.#batchBezig = this.#batchCadeausToCartEenmaal().finally(() => {
      this.#batchBezig = null;
    });
    return this.#batchBezig;
  }

  /** @returns {Promise<boolean>} */
  async #batchCadeausToCartEenmaal() {
    const gekozen = this.#geselecteerdeItems();
    if (!gekozen.length) return false;

    /** @type {Array<{ item: HTMLElement, naam: string, line: { id: string, quantity: number, properties: Record<string, string> } }>} */
    const keuzes = [];

    for (const item of gekozen) {
      const id = item.dataset.variantId ?? '';
      if (!id) continue;
      const row = this.#rows.find((kandidaat) => kandidaat.key === item.dataset.giftKey);
      keuzes.push({
        item,
        naam: item.dataset.giftName ?? '',
        line: {
          id,
          quantity: 1,
          properties: row?.properties ?? { _cross_sell: 'true', _is_free: 'false' },
        },
      });
    }
    if (!keuzes.length) return false;

    let gelukt = keuzes;
    let result;

    try {
      // Bewust níet in de gezamenlijke ronde van cart-batch.js: de checkout-regel weigert de seed
      // soms (drempel, tweede exemplaar) en dan zou de hele ronde één voor één terugvallen. Wel ná
      // die ronde, zodat de verzoeken niet tegelijk lopen.
      await whenCartBatchIdle();
      result = await this.#postCartAdd(keuzes.map((keuze) => keuze.line));
    } catch (error) {
      if (keuzes.length < 2 || /** @type {any} */ (error)?.status !== 422) {
        this.#meldGeweigerd(keuzes.map((keuze) => ({ naam: keuze.naam, reden: foutTekst(error) })), error);
        return false;
      }

      // Eén cadeau dat niet meer mag (bv. een tweede behangset) weigert de hele batch.
      // Los proberen, zodat de rest — de lijm — toch meegaat (SHO-11755).
      gelukt = [];
      /** @type {Array<{ naam: string, reden: string }>} */
      const geweigerd = [];
      for (const keuze of keuzes) {
        try {
          result = await this.#postCartAdd([keuze.line]);
          gelukt.push(keuze);
        } catch (losError) {
          geweigerd.push({ naam: keuze.naam, reden: foutTekst(losError) });
        }
      }
      if (geweigerd.length) this.#meldGeweigerd(geweigerd, error);
      if (!gelukt.length) return false;
    }

    try {
      document.dispatchEvent(
        new CartAddEvent(result, this.id || 'free-gifts', {
          source: 'free-gifts',
          itemCount: result.item_count,
          sections: result.sections,
        })
      );

      publiceerMeetEvent('hds:gift_add', {
        cadeaus: gelukt.map((keuze) => keuze.naam),
        aantal: gelukt.length,
        hoofdproduct_id: this.dataset.productId ?? '',
      });
    } catch (error) {
      console.warn('Gratis cadeaus — mand bijwerken na toevoegen mislukt:', error);
    }

    // Vinkjes uitzetten zonder select-events: dit is geen keuze van de klant.
    for (const { item } of gelukt) {
      const check = item.querySelector('[data-fg-check]');
      if (check instanceof HTMLInputElement) check.checked = false;
      item.classList.remove('fg-item--selected');
    }
    this.#updateTeller();
    return true;
  }

  /**
   * De Cart API draait de checkout-regel al: onder de drempel (bv. door een kortingscode
   * die wij hier niet zien) of bij een tweede exemplaar weigert hij de seed met een
   * nette melding. Dat is geen storing — het hoofdproduct ligt gewoon in de mand.
   * @param {Array<{ naam: string, reden: string }>} geweigerd
   * @param {unknown} error
   */
  #meldGeweigerd(geweigerd, error) {
    console.warn('Gratis cadeaus — meenemen bij In winkelwagen niet gelukt:', error);
    publiceerMeetEvent('hds:gift_add_refused', {
      cadeaus: geweigerd.map((weigering) => weigering.naam),
      reden: geweigerd[0]?.reden ?? foutTekst(error),
      hoofdproduct_id: this.dataset.productId ?? '',
    });
  }

  /**
   * @param {Array<{ id: string, quantity: number, properties: Record<string, string> }>} items
   * @param {boolean} [retryStringIds]
   * @returns {Promise<any>}
   */
  async #postCartAdd(items, retryStringIds = false) {
    const sectionIds = getBundledCartSectionIds();
    /** @type {Record<string, unknown>} */
    const body = {
      items: items.map((line) => ({
        id: retryStringIds ? line.id : Number(line.id) || line.id,
        quantity: line.quantity,
        properties: line.properties,
      })),
    };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error('Ongeldige reactie van winkelmand');
    }

    if (!response.ok || data.status) {
      if (response.status === 422 && !retryStringIds) return this.#postCartAdd(items, true);
      throw Object.assign(new Error(data.description || data.message || 'Toevoegen aan winkelmand mislukt'), {
        status: response.status,
      });
    }

    return data;
  }

  // ---------------------------------------------------------------- meten

  #observeerExposure() {
    if (this.#exposureGemeld || this.#exposureObserver) return;

    this.#exposureObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#exposureGemeld = true;
          this.#exposureObserver?.disconnect();
          this.#exposureObserver = undefined;
          publiceerMeetEvent('hds:gift_exposure', {
            aantal: this.#rows.length,
            cadeaus: this.#rows.map((row) => row.name),
            vergrendeld: this.#rows.filter((row) => row.locked).length,
            hoofdproduct_id: this.dataset.productId ?? '',
          });
          return;
        }
      },
      { threshold: 0.5 }
    );

    this.#exposureObserver.observe(this);
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    return formatMoneyFromCents(cents, this.dataset.moneyFormat || '{{amount}}').replace(',00', ',-');
  }
}

if (!customElements.get('free-gifts')) {
  customElements.define('free-gifts', FreeGifts);
}
